import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSignUp } from '../../hooks/useSignUp';
import { SignUpForm } from './components/SignUpForm';
import { useSignUpValidation, SignUpFormData } from './hooks/useSignUpValidation';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

const SignUp: React.FC = () => {
  const [formData, setFormData] = useState<SignUpFormData>({
    email: '',
    password: '',
    passwordConfirmation: '',
    fullName: '',
  });
  const { refetch } = useAuth();
  const { signUp, loading } = useSignUp();
  const navigate = useNavigate();
  const { errors, validate, clearErrors, setError: setValidationError } = useSignUpValidation();

  const handleFormChange = (field: keyof SignUpFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field-specific errors when user starts typing
    if (errors[field]) {
      clearErrors();
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearErrors();

    if (!validate(formData)) {
      return;
    }

    try {
      await signUp(formData.email, formData.password, formData.passwordConfirmation, formData.fullName);
      await refetch();
      navigate('/dashboard');
    } catch (err: any) {
      const errorMessage = err.graphQLErrors?.[0]?.message || 'Erro inesperado';
      setValidationError('general', errorMessage);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-50 dark:bg-primary-950 px-4">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-4xl font-light tracking-tight text-primary-950 dark:text-white">
            Criar sua conta
          </h2>
          <p className="mt-2 text-center text-sm text-primary-600 dark:text-primary-400">
            Comece sua jornada conosco
          </p>
        </div>

        <Card className="p-8">
          <CardContent className="p-0">
            <SignUpForm
              formData={formData}
              onChange={handleFormChange}
              onSubmit={handleSubmit}
              errors={errors}
              loading={loading}
            />

            <div className="mt-6">
              <div className="relative flex items-center">
                <Separator className="flex-1" />
                <span className="px-3 text-sm text-primary-500 dark:text-primary-500">
                  Já tem uma conta?
                </span>
                <Separator className="flex-1" />
              </div>

              <div className="mt-6">
                <Button asChild variant="outline" className="w-full">
                  <Link to="/login">Entrar em vez disso</Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SignUp;
